import React from 'react';
import { cn } from '@/lib/utils';

interface ContextualToolbarProps {
  children: React.ReactNode;
  className?: string;
  position?: 'top' | 'bottom' | 'sticky-top';
}

/**
 * ContextualToolbar - A reusable horizontal toolbar for contextual actions
 * 
 * Features:
 * - Full-width horizontal layout with visual emphasis
 * - Right-aligned buttons/controls by default
 * - Responsive design with mobile optimizations
 * - Configurable positioning (top, bottom, sticky)
 * - Distinct visual styling for pattern recognition
 * 
 * Usage:
 * ```tsx
 * <ContextualToolbar position="sticky-top">
 *   <Button onClick={handleAction1}>Action 1</Button>
 *   <Button onClick={handleAction2}>Action 2</Button>
 * </ContextualToolbar>
 * ```
 */
export function ContextualToolbar({
  children,
  className,
  position = 'top'
}: ContextualToolbarProps) {
  const baseClasses = "w-full bg-transparent";

  const positionClasses = {
    'top': '',
    'bottom': '',
    'sticky-top': 'sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80',
  };

  return (
    <div className={cn(
      baseClasses,
      positionClasses[position],
      className
    )}>
      <div className="flex items-center justify-end gap-1.5 px-3 py-2 md:gap-2 md:px-6 md:py-3">
        {children}
      </div>
    </div>
  );
}

interface ToolbarButtonGroupProps {
  children: React.ReactNode;
  className?: string;
  align?: 'left' | 'right' | 'center' | 'between';
}

/**
 * ToolbarButtonGroup - Groups buttons within the toolbar
 * 
 * Features:
 * - Configurable alignment
 * - Automatic spacing between buttons
 * - Mobile-responsive button sizing
 */
export function ToolbarButtonGroup({
  children,
  className,
  align = 'right'
}: ToolbarButtonGroupProps) {
  const alignClasses = {
    'left': 'justify-start',
    'right': 'justify-end',
    'center': 'justify-center',
    'between': 'justify-between'
  };

  return (
    <div className={cn(
      "flex items-center gap-2 flex-wrap",
      alignClasses[align],
      className
    )}>
      {children}
    </div>
  );
}

interface ToolbarSeparatorProps {
  className?: string;
}

/**
 * ToolbarSeparator - Visual separator between toolbar sections
 */
export function ToolbarSeparator({ className }: ToolbarSeparatorProps) {
  return (
    <div
      className={cn(
        "h-5 md:h-6 w-px bg-border mx-1 self-center",
        className
      )}
      role="separator"
      aria-orientation="vertical"
    />
  );
}

